import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import './RecentOrders.css';

const RecentOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentOrders();
  }, []);

  const fetchRecentOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching recent orders:', error);
        setOrders([]);
        setLoading(false);
        return;
      }

      // Transform Supabase data to component format
      const transformedOrders = (data || []).map((order, index) => {
        // Parse items if it's a string
        const orderItems = typeof order.items === 'string' 
          ? JSON.parse(order.items) 
          : order.items;

        // Map database status to component status
        const statusMap = {
          'pending': 'New',
          'confirmed': 'Preparing',
          'preparing': 'Preparing',
          'ready': 'Ready',
          'out_for_delivery': 'Ready',
          'delivered': 'Delivered',
          'cancelled': 'Cancelled'
        };

        const componentStatus = statusMap[order.status.toLowerCase()] || 'New';

        const orderDate = new Date(order.created_at);
        const timeString = orderDate.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });

        const itemCount = orderItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

        return {
          id: order.id.slice(0, 8).toUpperCase(),
          fullId: order.id,
          customer: order.customer_name,
          phone: order.customer_phone || 'N/A',
          items: `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`,
          time: timeString,
          amount: `Rs. ${parseFloat(order.total).toFixed(2)}`,
          status: componentStatus
        };
      });

      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'New':
        return 'status-new';
      case 'Preparing':
        return 'status-preparing';
      case 'Delivered':
        return 'status-delivered';
      default:
        return '';
    }
  };

  const handleViewAll = () => {
    navigate('/admin/orders');
  };

  const handleOrderClick = (orderId) => {
    navigate(`/admin/orders/${orderId}`);
  };

  if (loading) {
    return (
      <div className="recent-orders-section">
        <div className="section-header">
          <h2 className="section-title">Recent Orders</h2>
          <button className="view-all-link" onClick={handleViewAll}>View All</button>
        </div>
        <div className="orders-table-wrapper">
          <div className="loading-state">Loading orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-orders-section">
      <div className="section-header">
        <h2 className="section-title">Recent Orders</h2>
        <button className="view-all-link" onClick={handleViewAll}>View All</button>
      </div>

      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={index} onClick={() => handleOrderClick(order.fullId)} style={{ cursor: 'pointer' }}>
                  <td>
                    <span className="order-id">
                      #{order.id}
                    </span>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{order.customer}</div>
                      <div className="customer-phone">{order.phone}</div>
                    </div>
                  </td>
                  <td>
                    <div className="items-info">
                      {order.items} • {order.time}
                    </div>
                  </td>
                  <td>
                    <div className="amount">{order.amount}</div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="btn-view"
                        onClick={() => handleOrderClick(order.fullId)}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="empty-state">
                  <div className="empty-state-content">
                    <span className="empty-icon">📦</span>
                    <p>No recent orders</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RecentOrders;

